import { useState } from 'react'
import { asMessage } from '../shared/utils/errorDetail'

import apiClient from '../api/index.js'

/**
 * Change your own password, from the System Admin Portal.
 *
 * WHY IT EXISTS. The portal had no account controls at all — only a tenant list and Logout. On
 * 2026-08-19 the owner was locked out of admin.tynvara.com, a temporary password had to be set for
 * him from outside the app, and when he got in there was still no way to change it. A credential
 * somebody else issued, that its holder cannot rotate, is a credential nobody can take back.
 *
 * THE RULES ARE STATED, NOT DISCOVERED. `PasswordService.is_strong_password` refuses on the FIRST
 * unmet rule, so a form that just forwards the error teaches them one at a time: too short, then no
 * capital, then no digit, then no symbol — four rejected attempts to learn one policy. They are
 * listed up front and ticked live instead.
 */
const RULES = [
  { label: 'At least 8 characters', ok: (p) => p.length >= 8 },
  { label: 'A capital letter', ok: (p) => /[A-Z]/.test(p) },
  { label: 'A lowercase letter', ok: (p) => /[a-z]/.test(p) },
  { label: 'A number', ok: (p) => /[0-9]/.test(p) },
  // The exact set the backend accepts. A form that allowed more would reject on submit for a
  // character the user was invited to type.
  { label: 'A symbol, e.g. ! @ # $ % ^ & *', ok: (p) => /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(p) },
]

export default function ChangeMyPassword() {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)

  const unmet = RULES.filter(r => !r.ok(next))
  const mismatch = again.length > 0 && next !== again
  const canSubmit = current && next && !unmet.length && !mismatch && !busy

  const submit = async (e) => {
    e.preventDefault()
    setBusy(true); setError('')
    try {
      await apiClient.post('/auth/change-password', {
        current_password: current,
        new_password: next,
      })
      setDone(true)
      setCurrent(''); setNext(''); setAgain('')
    } catch (err) {
      setError(asMessage(err.response?.data?.detail) || 'Could not change the password.')
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return (
      <div className="rounded-xl border border-green-200 bg-green-50 p-6">
        <h2 className="text-lg font-bold text-green-900">Password changed</h2>
        <p className="mt-1 text-sm text-green-800">
          Use the new one next time you sign in. If anyone else had the old password, it no longer
          works.
        </p>
        <button onClick={() => setDone(false)}
                className="mt-3 text-sm font-medium text-green-900 underline hover:no-underline">
          Change it again
        </button>
      </div>
    )
  }

  return (
    <form onSubmit={submit} className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
      <h2 className="text-lg font-bold text-gray-900">Your password</h2>
      <p className="mt-1 text-sm text-gray-500">
        Change the password you use to sign in to this portal.
      </p>

      <div className="mt-5 grid gap-4 sm:max-w-md">
        <label className="block">
          <span className="mb-1 block text-sm font-semibold text-gray-700">Current password</span>
          <input type="password" value={current} onChange={e => setCurrent(e.target.value)}
                 autoComplete="current-password"
                 className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-semibold text-gray-700">New password</span>
          <input type="password" value={next} onChange={e => setNext(e.target.value)}
                 autoComplete="new-password"
                 className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
        </label>

        <ul className="space-y-1">
          {RULES.map(r => {
            const ok = r.ok(next)
            return (
              <li key={r.label}
                  className={`flex items-center gap-2 text-xs ${
                    !next ? 'text-gray-400' : ok ? 'text-green-700' : 'text-gray-500'}`}>
                <span aria-hidden="true">{next && ok ? '✓' : '•'}</span>
                {r.label}
              </li>
            )
          })}
        </ul>

        <label className="block">
          <span className="mb-1 block text-sm font-semibold text-gray-700">New password again</span>
          <input type="password" value={again} onChange={e => setAgain(e.target.value)}
                 autoComplete="new-password"
                 className={`w-full rounded-lg border px-3 py-2 text-sm ${
                   mismatch ? 'border-red-400' : 'border-gray-300'}`} />
          {mismatch && (
            <span className="mt-1 block text-xs text-red-700">These two do not match.</span>
          )}
        </label>

        {error && <p className="text-sm text-red-700">{error}</p>}

        <div>
          <button type="submit" disabled={!canSubmit}
                  className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-black disabled:opacity-40">
            {busy ? 'Changing…' : 'Change password'}
          </button>
        </div>
      </div>
    </form>
  )
}
